import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { KeyRound } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Voluntary password-change form on the Account page.
 *
 * Requires the caller to type their CURRENT password so that a
 * transiently-stolen session token can't silently lock the real
 * owner out. The forgot-password flow (email → magic link) remains
 * the "I don't know my current password" escape hatch.
 */
export default function ChangePasswordForm() {
  const { t } = useTranslation();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);

  const canSubmit =
    currentPw.length > 0 &&
    newPw.length >= 6 &&
    newPw === confirmPw &&
    newPw !== currentPw;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success(t("changePassword.updated"));
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="account-change-password"
      className="bg-white border border-slate-200 rounded-2xl p-8"
    >
      <div className="flex items-start gap-3 mb-1">
        <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0 mt-0.5">
          <KeyRound size={16} className="text-blue-800" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">
            {t("changePassword.title")}
          </div>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">
            {t("changePassword.body")}
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4 max-w-md">
        <div>
          <Label htmlFor="current-password" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            {t("changePassword.current")}
          </Label>
          <PasswordInput
            id="current-password"
            data-testid="change-password-current"
            autoComplete="current-password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            className="mt-1 h-11"
            required
          />
        </div>
        <div>
          <Label htmlFor="new-password" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            {t("changePassword.newLabel")} <span className="text-slate-400 normal-case">{t("changePassword.newHint")}</span>
          </Label>
          <PasswordInput
            id="new-password"
            data-testid="change-password-new"
            autoComplete="new-password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            className="mt-1 h-11"
            minLength={6}
            required
          />
        </div>
        <div>
          <Label htmlFor="confirm-password" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            {t("changePassword.confirm")}
          </Label>
          <PasswordInput
            id="confirm-password"
            data-testid="change-password-confirm"
            autoComplete="new-password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            className="mt-1 h-11"
            required
          />
          {confirmPw && confirmPw !== newPw && (
            <p className="mt-1 text-xs text-rose-600">{t("changePassword.mismatch")}</p>
          )}
        </div>
        <Button
          type="submit"
          data-testid="change-password-submit"
          disabled={!canSubmit || saving}
          className="bg-blue-800 hover:bg-blue-700 text-white h-11 px-6"
        >
          {saving ? t("changePassword.updating") : t("changePassword.submit")}
        </Button>
      </form>
    </div>
  );
}
